// CHAIN REACTION — every answer has to link to the one before it.
//
// Shape: { id, topic, difficulty, template, seed, links[] }
// where each link is { prompt, options[4], answer, reason }.
//
// WHY MULTIPLE CHOICE. The brief describes typing a free answer that relates
// to the last one. Marking that offline means either a dictionary of every
// acceptable spelling of every footballer's name, or a game that tells an
// eleven-year-old he is wrong because he typed "Bayern Munchen". Four options
// per link keeps the actual thinking — which of these follows from the last
// answer — and drops the spelling test. The screen shuffles the options, so
// `answer` being 0 throughout is not a tell.
//
// A chain BREAKS on a wrong link and the run ends. Longer chain, more points.

export default [

  // --- STRETCHING: for a five-to-eight-year-old ------------------------------
  // Short chains of three links, each one following obviously from the last, so
  // the CHAIN idea lands without needing the knowledge.
  {
    id: 'ch-st-1', topic: 'animals', difficulty: 'stretching',
    template: 'Animal → Sound → Home',
    seed: 'Farm',
    links: [
      { prompt: 'An animal you find on a farm', options: ['Cow', 'Lion', 'Penguin', 'Shark'], answer: 0, reason: 'Cows live on farms. The others really do not.' },
      { prompt: 'The sound a cow makes', options: ['Moo', 'Woof', 'Tweet', 'Roar'], answer: 0, reason: 'Moo. Everyone knows this one.' },
      { prompt: 'Where a cow sleeps', options: ['A barn', 'A nest', 'The sea', 'A web'], answer: 0, reason: 'A barn, out of the rain.' },
    ],
  },
  {
    id: 'ch-st-2', topic: 'food', difficulty: 'stretching',
    template: 'Colour → Fruit → Taste',
    seed: 'Yellow',
    links: [
      { prompt: 'A fruit that is yellow', options: ['Banana', 'Strawberry', 'Blueberry', 'Lime'], answer: 0, reason: 'Bananas are yellow when they are ready.' },
      { prompt: 'What you do before eating a banana', options: ['Peel it', 'Boil it', 'Freeze it', 'Bounce it'], answer: 0, reason: 'Peel the skin off first.' },
      { prompt: 'An animal famous for eating bananas', options: ['Monkey', 'Cat', 'Sheep', 'Owl'], answer: 0, reason: 'Monkeys and bananas go together.' },
    ],
  },
  {
    id: 'ch-st-3', topic: 'space', difficulty: 'stretching',
    template: 'Sky → Thing → Fact',
    seed: 'Sky',
    links: [
      { prompt: 'Something you see in the sky in the daytime', options: ['The Sun', 'The Moon', 'A whale', 'A tree'], answer: 0, reason: 'The Sun is the bright one in the daytime.' },
      { prompt: 'What the Sun gives us', options: ['Light and heat', 'Rain', 'Sand', 'Snow'], answer: 0, reason: 'Light to see by and heat to keep warm.' },
      { prompt: 'The planet we live on', options: ['Earth', 'Mars', 'The Moon', 'The Sun'], answer: 0, reason: 'Earth. The only one with us on it.' },
    ],
  },
  {
    id: 'ch-st-4', topic: 'animals', difficulty: 'stretching',
    template: 'Water → Animal → Fact',
    seed: 'Sea',
    links: [
      { prompt: 'An animal that lives in the sea', options: ['Fish', 'Dog', 'Cow', 'Spider'], answer: 0, reason: 'Fish live in water all the time.' },
      { prompt: 'What a fish uses to swim', options: ['Fins', 'Legs', 'Wings', 'Wheels'], answer: 0, reason: 'Fins push the water and the fish moves.' },
      { prompt: 'A very big animal that lives in the sea', options: ['Whale', 'Mouse', 'Ant', 'Rabbit'], answer: 0, reason: 'A whale is the biggest animal there has ever been.' },
    ],
  },

  {
    id: 'ch-1', topic: 'football', difficulty: 'sweaty',
    template: 'Letter → Country → Player → Club',
    seed: 'G',
    links: [
      { prompt: 'A country beginning with G', options: ['Germany', 'France', 'Spain', 'Italy'], answer: 0, reason: 'Germany. France, Spain and Italy start with other letters.' },
      { prompt: 'A footballer who plays for Germany', options: ['Jamal Musiala', 'Kylian Mbappé', 'Bukayo Saka', 'Vinícius Júnior'], answer: 0, reason: 'Musiala plays for the German national team.' },
      { prompt: 'The club Jamal Musiala plays for', options: ['Bayern Munich', 'Real Madrid', 'Arsenal', 'Paris Saint-Germain'], answer: 0, reason: 'Bayern Munich, in the Bundesliga.' },
      { prompt: 'The city Bayern Munich play in', options: ['Munich', 'Berlin', 'Hamburg', 'Dortmund'], answer: 0, reason: 'It is in the name, to be fair.' },
    ],
  },
  {
    id: 'ch-2', topic: 'football', difficulty: 'sweaty',
    template: 'Letter → Country → Player → Club',
    seed: 'E',
    links: [
      { prompt: 'A country beginning with E', options: ['England', 'Portugal', 'Belgium', 'Croatia'], answer: 0, reason: 'England.' },
      { prompt: 'A footballer who plays for England', options: ['Jude Bellingham', 'Erling Haaland', 'Lionel Messi', 'Luka Modrić'], answer: 0, reason: 'Bellingham is an England international.' },
      { prompt: 'The club Jude Bellingham plays for', options: ['Real Madrid', 'Manchester City', 'Liverpool', 'Barcelona'], answer: 0, reason: 'He joined Real Madrid from Borussia Dortmund.' },
      { prompt: 'The country Real Madrid play in', options: ['Spain', 'Portugal', 'Italy', 'France'], answer: 0, reason: 'La Liga, in Spain.' },
    ],
  },
  {
    id: 'ch-3', topic: 'animals', difficulty: 'warm-up',
    template: 'Country → Native animal → Fact',
    seed: 'AUSTRALIA',
    links: [
      { prompt: 'An animal native to Australia', options: ['Kangaroo', 'Panda', 'Moose', 'Tiger'], answer: 0, reason: 'Kangaroos are found in the wild only in Australia and New Guinea.' },
      { prompt: 'What makes a kangaroo a marsupial', options: ['It carries its young in a pouch', 'It lays eggs', 'It has no backbone', 'It breathes through its skin'], answer: 0, reason: 'A joey lives in the pouch for months after being born.' },
      { prompt: 'Something a kangaroo genuinely cannot do', options: ['Walk backwards', 'Swim', 'Jump', 'Stand on its tail'], answer: 0, reason: 'Its tail and back legs make going backwards almost impossible.' },
    ],
  },
  {
    id: 'ch-4', topic: 'animals', difficulty: 'sweaty',
    template: 'Country → Native animal → Fact',
    seed: 'NEW ZEALAND',
    links: [
      { prompt: 'A bird native to New Zealand', options: ['Kiwi', 'Emu', 'Ostrich', 'Puffin'], answer: 0, reason: 'The kiwi is New Zealand’s national bird.' },
      { prompt: 'Something unusual about the kiwi', options: ['It cannot fly', 'It hunts in packs', 'It lives in the sea', 'It migrates to Africa'], answer: 0, reason: 'Flightless, nocturnal, and it has nostrils at the tip of its beak.' },
      { prompt: 'Another flightless bird', options: ['Penguin', 'Eagle', 'Swallow', 'Falcon'], answer: 0, reason: 'Penguins swim instead — their wings became flippers.' },
    ],
  },
  {
    id: 'ch-5', topic: 'music', difficulty: 'sweaty',
    template: 'Band → Album → Year',
    seed: 'THE BEATLES',
    links: [
      { prompt: 'An album by The Beatles', options: ['Abbey Road', 'Back in Black', 'Thriller', 'Nevermind'], answer: 0, reason: 'Abbey Road — the one with the zebra crossing on the cover.' },
      { prompt: 'The year Abbey Road came out', options: ['1969', '1975', '1980', '1991'], answer: 0, reason: 'The same year as the Moon landing.' },
      { prompt: 'The city The Beatles came from', options: ['Liverpool', 'London', 'Manchester', 'Dublin'], answer: 0, reason: 'All four of them.' },
    ],
  },
  {
    id: 'ch-6', topic: 'music', difficulty: 'fully-austinated',
    template: 'Band → Album → Year',
    seed: 'AC/DC',
    links: [
      { prompt: 'An album by AC/DC', options: ['Back in Black', 'Abbey Road', 'Rumours', 'Parachutes'], answer: 0, reason: 'One of the best-selling albums ever made.' },
      { prompt: 'The year Back in Black came out', options: ['1980', '1969', '1991', '2001'], answer: 0, reason: '1980.' },
      { prompt: 'The city AC/DC formed in', options: ['Sydney', 'London', 'Glasgow', 'Los Angeles'], answer: 0, reason: 'Sydney, Australia, in 1973.' },
    ],
  },
  {
    id: 'ch-7', topic: 'space', difficulty: 'warm-up',
    template: 'Planet → Feature → Fact',
    seed: 'JUPITER',
    links: [
      { prompt: 'A famous feature of Jupiter', options: ['The Great Red Spot', 'The rings of ice', 'Olympus Mons', 'The Sea of Tranquility'], answer: 0, reason: 'A storm that has been running for centuries.' },
      { prompt: 'What the Great Red Spot actually is', options: ['A storm', 'A crater', 'A desert', 'An ocean'], answer: 0, reason: 'And it is wider than the whole Earth.' },
      { prompt: 'How Jupiter compares to the other planets', options: ['It is the largest', 'It is the smallest', 'It is the hottest', 'It is the closest to the Sun'], answer: 0, reason: 'More massive than all the other planets put together.' },
    ],
  },
  {
    id: 'ch-8', topic: 'space', difficulty: 'sweaty',
    template: 'Planet → Feature → Fact',
    seed: 'MARS',
    links: [
      { prompt: 'A famous feature of Mars', options: ['Olympus Mons', 'The Great Red Spot', 'The rings', 'The Cassini Division'], answer: 0, reason: 'The tallest volcano in the solar system.' },
      { prompt: 'What Olympus Mons is', options: ['A volcano', 'A canyon', 'A crater lake', 'An ice cap'], answer: 0, reason: 'About three times the height of Everest.' },
      { prompt: 'Why Mars looks red', options: ['Rust in its soil', 'It is on fire', 'Red clouds', 'It reflects the Sun'], answer: 0, reason: 'Iron oxide — the same stuff as rust on a bike.' },
    ],
  },
  {
    id: 'ch-9', topic: 'geography', difficulty: 'sweaty',
    template: 'Letter → Country → Capital → Landmark',
    seed: 'A',
    links: [
      { prompt: 'A landlocked country beginning with A', options: ['Austria', 'Australia', 'Argentina', 'Algeria'], answer: 0, reason: 'Austria. Australia is surrounded by ocean, so it is not landlocked.' },
      { prompt: 'The capital of Austria', options: ['Vienna', 'Salzburg', 'Innsbruck', 'Graz'], answer: 0, reason: 'Vienna, on the Danube.' },
      { prompt: 'A composer born in Austria', options: ['Mozart', 'Beethoven', 'Bach', 'Chopin'], answer: 0, reason: 'Born in Salzburg in 1756.' },
    ],
  },
  {
    id: 'ch-10', topic: 'geography', difficulty: 'warm-up',
    template: 'Letter → Country → Capital → Landmark',
    seed: 'A',
    links: [
      { prompt: 'A country beginning with A that is also a continent', options: ['Australia', 'Austria', 'Argentina', 'Angola'], answer: 0, reason: 'Australia manages both at once.' },
      { prompt: 'The capital of Australia', options: ['Canberra', 'Sydney', 'Melbourne', 'Perth'], answer: 0, reason: 'Sydney and Melbourne could not agree, so they built Canberra in between.' },
      { prompt: 'A famous landmark in Australia', options: ['The Sydney Opera House', 'The Eiffel Tower', 'Machu Picchu', 'The Colosseum'], answer: 0, reason: 'Those white shells on Sydney Harbour.' },
    ],
  },
  {
    id: 'ch-11', topic: 'football', difficulty: 'sweaty',
    template: 'Sport → Country that dominates it → Athlete',
    seed: 'SPRINTING',
    links: [
      { prompt: 'The country that has dominated sprinting', options: ['Jamaica', 'Norway', 'India', 'Egypt'], answer: 0, reason: 'A small island that keeps producing the fastest people alive.' },
      { prompt: 'The most famous Jamaican sprinter', options: ['Usain Bolt', 'Michael Phelps', 'Mo Farah', 'Eliud Kipchoge'], answer: 0, reason: 'Still holds the 100 m and 200 m world records.' },
      { prompt: 'The distance Usain Bolt is most famous for', options: ['100 metres', 'The marathon', '1500 metres', '10,000 metres'], answer: 0, reason: '9.58 seconds, set in 2009.' },
    ],
  },
  {
    id: 'ch-12', topic: 'football', difficulty: 'fully-austinated',
    template: 'Sport → Country that dominates it → Athlete',
    seed: 'RUGBY UNION',
    links: [
      { prompt: 'The country that has dominated rugby union', options: ['New Zealand', 'Brazil', 'Germany', 'Norway'], answer: 0, reason: 'The All Blacks.' },
      { prompt: 'What New Zealand’s rugby team is called', options: ['The All Blacks', 'The Wallabies', 'The Springboks', 'The Pumas'], answer: 0, reason: 'Australia are the Wallabies, South Africa the Springboks.' },
      { prompt: 'The dance the All Blacks perform before a match', options: ['The haka', 'The tango', 'The waltz', 'The hongi'], answer: 0, reason: 'A Māori ceremonial challenge.' },
    ],
  },
  {
    id: 'ch-13', topic: 'history', difficulty: 'sweaty',
    template: 'Event → Year → What else happened',
    seed: 'THE MOON LANDING',
    links: [
      { prompt: 'The year of the first Moon landing', options: ['1969', '1959', '1972', '1981'], answer: 0, reason: 'Apollo 11, July 1969.' },
      { prompt: 'The first person to walk on the Moon', options: ['Neil Armstrong', 'Buzz Aldrin', 'Yuri Gagarin', 'Michael Collins'], answer: 0, reason: 'Aldrin followed him about twenty minutes later.' },
      { prompt: 'The country that sent Apollo 11', options: ['The United States', 'The Soviet Union', 'China', 'The United Kingdom'], answer: 0, reason: 'NASA, at the height of the space race.' },
    ],
  },
  {
    id: 'ch-14', topic: 'gaming', difficulty: 'warm-up',
    template: 'Game → Company → Fact',
    seed: 'MARIO',
    links: [
      { prompt: 'The company that makes Mario games', options: ['Nintendo', 'Sega', 'Sony', 'Microsoft'], answer: 0, reason: 'Mario is Nintendo’s mascot.' },
      { prompt: 'What Nintendo originally made', options: ['Playing cards', 'Bicycles', 'Televisions', 'Cars'], answer: 0, reason: 'Founded in 1889, decades before video games existed.' },
      { prompt: 'Mario’s job', options: ['Plumber', 'Chef', 'Doctor', 'Builder'], answer: 0, reason: 'Although he has also been a doctor, a referee and a go-kart driver.' },
    ],
  },
];
